import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Library, Plus, Trash2, Target, Stethoscope, Loader2, Users, Check } from "lucide-react";

export default function LibraryManager() {
  const [items, setItems] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goalText, setGoalText] = useState("");
  const [intvText, setIntvText] = useState("");
  const [selItems, setSelItems] = useState([]); // library item ids
  const [selConsumers, setSelConsumers] = useState([]);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/library"), api.get("/consumers")]);
      setItems(a.data);
      setConsumers(b.data);
    } catch {
      toast.error("Could not load library.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (type, text, reset) => {
    if (!text.trim()) return;
    try {
      await api.post("/library", { type, text });
      reset("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/library/${id}`);
      setSelItems((s) => s.filter((x) => x !== id));
      await load();
    } catch {
      toast.error("Could not delete item.");
    }
  };

  const toggle = (arr, set, id) =>
    set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const apply = async () => {
    if (!selItems.length || !selConsumers.length)
      return toast.info("Select at least one item and one consumer.");
    setApplying(true);
    const chosen = items.filter((i) => selItems.includes(i.id));
    const goals = chosen.filter((i) => i.type === "goal").map((i) => i.text);
    const interventions = chosen.filter((i) => i.type === "intervention").map((i) => i.text);
    try {
      const { data } = await api.post("/library/apply", {
        goals, interventions, consumer_ids: selConsumers,
      });
      toast.success(`Applied to ${data.updated} consumer(s).`);
      setSelItems([]);
      setSelConsumers([]);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setApplying(false);
    }
  };

  const goals = items.filter((i) => i.type === "goal");
  const intvs = items.filter((i) => i.type === "intervention");

  const ItemRow = ({ i }) => {
    const on = selItems.includes(i.id);
    return (
      <div data-testid={`library-item-${i.id}`} className={`group flex items-center gap-2 p-2.5 rounded-lg border transition-colors ${on ? "bg-[#DCE5DF] border-[#4A6B53]" : "bg-white border-[#E2DFD8]"}`}>
        <button data-testid={`library-toggle-${i.id}`} onClick={() => toggle(selItems, setSelItems, i.id)} className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-[#4A6B53] border-[#4A6B53]" : "border-[#C9C6BE]"}`}>
          {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
        </button>
        <span className="text-sm text-[#3A453F] flex-1 min-w-0">{i.text}</span>
        <button data-testid={`library-delete-${i.id}`} onClick={() => remove(i.id)} className="opacity-0 group-hover:opacity-100 text-[#7A8C82] hover:text-[#C26D5C] transition-opacity">
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      </div>
    );
  };

  return (
    <div data-testid="library-manager">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
          <Library className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">
            Shared Goal & Intervention Library
          </h1>
          <p className="text-sm text-[#7A8C82]">
            Save goals & interventions once, then apply the common ones to many consumers at a time.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* library items */}
        <div className="col-span-12 lg:col-span-7 space-y-6">
          <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-5">
            <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider mb-3 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" strokeWidth={1.5} /> Goals</p>
            <div className="flex gap-2 mb-3">
              <Input data-testid="library-goal-input" value={goalText} onChange={(e) => setGoalText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add("goal", goalText, setGoalText)} placeholder="e.g. Improve daily living skills" className="h-10 bg-[#F9F9F6] border-[#E2DFD8] text-sm" />
              <Button data-testid="library-add-goal-btn" onClick={() => add("goal", goalText, setGoalText)} className="h-10 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white shrink-0"><Plus className="w-4 h-4" strokeWidth={2} /></Button>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {goals.length ? goals.map((i) => <ItemRow key={i.id} i={i} />) : <p className="text-sm text-[#7A8C82]">No goals yet.</p>}
            </div>
          </div>

          <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-5">
            <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider mb-3 flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" strokeWidth={1.5} /> Interventions</p>
            <div className="flex gap-2 mb-3">
              <Input data-testid="library-intervention-input" value={intvText} onChange={(e) => setIntvText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add("intervention", intvText, setIntvText)} placeholder="e.g. Coping-skills coaching" className="h-10 bg-[#F9F9F6] border-[#E2DFD8] text-sm" />
              <Button data-testid="library-add-intervention-btn" onClick={() => add("intervention", intvText, setIntvText)} className="h-10 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white shrink-0"><Plus className="w-4 h-4" strokeWidth={2} /></Button>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {intvs.length ? intvs.map((i) => <ItemRow key={i.id} i={i} />) : <p className="text-sm text-[#7A8C82]">No interventions yet.</p>}
            </div>
          </div>
        </div>

        {/* apply to consumers */}
        <div className="col-span-12 lg:col-span-5">
          <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-5 lg:sticky lg:top-24">
            <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider mb-1 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Apply to consumers</p>
            <p className="text-xs text-[#7A8C82] mb-3">Tick items on the left, then tick the consumers to add them to.</p>
            <div className="space-y-1.5 max-h-72 overflow-y-auto mb-4">
              {consumers.map((c) => {
                const on = selConsumers.includes(c.id);
                return (
                  <button key={c.id} data-testid={`library-consumer-${c.id}`} onClick={() => toggle(selConsumers, setSelConsumers, c.id)} className={`w-full flex items-center gap-2 p-2.5 rounded-lg border text-left transition-colors ${on ? "bg-[#DCE5DF] border-[#4A6B53]" : "bg-[#F9F9F6] border-[#E2DFD8]"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-[#4A6B53] border-[#4A6B53]" : "border-[#C9C6BE]"}`}>
                      {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </span>
                    <span className="text-sm text-[#121814] truncate">{c.name}</span>
                  </button>
                );
              })}
              {!consumers.length && <p className="text-sm text-[#7A8C82]">No consumers yet.</p>}
            </div>
            <p className="text-xs text-[#7A8C82] mb-2">
              {selItems.length} item(s) → {selConsumers.length} consumer(s)
            </p>
            <Button data-testid="library-apply-btn" onClick={apply} disabled={applying} className="w-full h-11 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply to selected"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
